import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, BookOpen, CheckCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const OAUTH_ERRORS = {
  microsoft_cancelled: 'Connexion Microsoft annulée.',
  microsoft_token_failed: 'Erreur lors de la connexion Microsoft.',
  microsoft_no_email: 'Votre compte Microsoft ne fournit pas d\'adresse email.',
  facebook_cancelled: 'Connexion Facebook annulée.',
  facebook_token_failed: 'Erreur lors de la connexion Facebook.',
  facebook_no_email: 'Votre compte Facebook doit avoir un email lié.',
};

function formatError(detail) {
  if (!detail) return 'Une erreur est survenue. Réessayez.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function Auth() {
  const { t, i18n } = useTranslation();
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'register' : 'login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  // Handle OAuth errors returned in URL params
  useEffect(() => {
    const oauthError = searchParams.get('oauth_error');
    if (oauthError && OAUTH_ERRORS[oauthError]) {
      toast.error(OAUTH_ERRORS[oauthError]);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
      }
      const state = location.state;
      if (state?.topic) {
        const { default: ax } = await import('axios');
        const { data } = await ax.post(`${API}/api/courses/generate`, { topic: state.topic }, { withCredentials: true });
        navigate(`/generate/${data.course_id}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch {
      setForgotSent(true); // Always show success to prevent email enumeration
    } finally {
      setForgotLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${API}/api/auth/oauth/microsoft`;
  };

  const handleFacebookLogin = () => {
    window.location.href = `${API}/api/auth/oauth/facebook`;
  };

  const switchToForgot = () => {
    setForgotMode(true);
    setForgotEmail(form.email || '');
    setError('');
  };

  return (
    <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center px-4 py-12">
      <div className="absolute top-20 left-1/4 w-64 h-64 bg-[#ff6b6b]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-1/4 w-48 h-48 bg-[#20c997]/5 rounded-full blur-3xl pointer-events-none" />

      <button onClick={() => navigate('/')} className="font-heading font-bold text-2xl text-white mb-10" data-testid="auth-logo">
        Kurso
      </button>

      <div className="w-full max-w-sm">
        <div className="kurso-card p-8 animate-fade-up">
          <div className="flex items-center justify-center w-12 h-12 bg-[#ff6b6b]/10 rounded-2xl mb-6 mx-auto">
            <BookOpen size={22} className="text-[#ff6b6b]" />
          </div>

          {/* ---- FORGOT PASSWORD MODE ---- */}
          {forgotMode ? (
            forgotSent ? (
              <div className="text-center" data-testid="forgot-success">
                <CheckCircle size={36} className="text-[#06d6a0] mx-auto mb-3" />
                <h2 className="font-heading text-xl font-bold text-white mb-2">{t('auth.forgotSuccessTitle')}</h2>
                <p className="text-[#a0a0ab] text-sm mb-4">
                  {t('auth.forgotSuccessText')}
                </p>
                <button onClick={() => { setForgotMode(false); setForgotSent(false); }}
                  className="text-sm text-[#ff6b6b] hover:underline">
                  {t('auth.forgotBackLink')}
                </button>
              </div>
            ) : (
              <>
                <h1 className="font-heading text-xl font-bold text-white text-center mb-1">{t('auth.forgotTitle')}</h1>
                <p className="text-[#a0a0ab] text-sm text-center mb-6">
                  {t('auth.forgotSubtitle')}
                </p>
                <form onSubmit={handleForgotSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm text-[#e8e8e8] mb-1.5 block">{t('auth.forgotEmailLabel')}</label>
                    <input
                      type="email" value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      placeholder="marie@exemple.fr" required
                      data-testid="forgot-email-input"
                      className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#ff6b6b]/50 transition-colors"
                    />
                  </div>
                  <button type="submit" disabled={forgotLoading}
                    data-testid="forgot-submit-btn"
                    className="btn-primary w-full py-3.5 text-sm disabled:opacity-50">
                    {forgotLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 rounded-full border-2 border-[#0f0e17] border-t-transparent animate-spin" />
                        {t('auth.forgotSending')}
                      </span>
                    ) : t('auth.forgotSendBtn')}
                  </button>
                </form>
                <p className="text-center mt-4">
                  <button onClick={() => setForgotMode(false)} className="text-sm text-[#a0a0ab] hover:text-[#ff6b6b] transition-colors">
                    {t('auth.forgotBackLink')}
                  </button>
                </p>
              </>
            )
          ) : (
            /* ---- MAIN LOGIN / REGISTER ---- */
            <>
              <h1 className="font-heading text-2xl font-bold text-white text-center mb-1">
                {mode === 'login' ? t('auth.loginTitle') : t('auth.registerTitle')}
              </h1>
              <p className="text-[#e8e8e8] text-sm text-center mb-6">
                {mode === 'login' ? t('auth.loginSubtitle') : t('auth.registerSubtitle')}
              </p>

              {/* Social Login Buttons */}
              <div className="space-y-2 mb-4">
                <button onClick={handleGoogleLogin} type="button"
                  data-testid="google-login-btn"
                  className="w-full flex items-center justify-center gap-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl py-2.5 text-sm text-white transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {t('auth.socialGoogle')}                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={handleMicrosoftLogin} type="button"
                    data-testid="microsoft-login-btn"
                    className="hidden flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl py-2.5 text-xs text-white transition-colors">
                    <svg width="14" height="14" viewBox="0 0 21 21">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                    </svg>
                    Microsoft
                  </button>
                  <button onClick={handleFacebookLogin} type="button"
                    data-testid="facebook-login-btn"
                    className="hidden flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl py-2.5 text-xs text-white transition-colors">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#1877F2">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-[#a0a0ab]">{t('auth.socialDivider')}</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'register' && (
                  <div>
                    <label className="text-sm text-[#e8e8e8] mb-1.5 block">{t('auth.name')}</label>
                    <input
                      type="text" value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="Marie Dupont" required
                      className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#20c997]/50 transition-colors"
                      data-testid="register-name-input"
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm text-[#e8e8e8] mb-1.5 block">{t('auth.email')}</label>
                  <input
                    type="email" value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="marie@exemple.fr" required
                    className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#20c997]/50 transition-colors"
                    data-testid="auth-email-input"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm text-[#e8e8e8]">{t('auth.password')}</label>
                    {mode === 'login' && (
                      <button type="button" onClick={switchToForgot}
                        data-testid="forgot-password-link"
                        className="text-xs text-[#a0a0ab] hover:text-[#ff6b6b] transition-colors">
                        {t('auth.forgotPasswordLink')}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      placeholder={t('auth.passwordMin')} required minLength={6}
                      className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:border-[#20c997]/50 transition-colors"
                      data-testid="auth-password-input"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#e8e8e8] hover:text-white transition-colors"
                      data-testid="toggle-password-btn">
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2 text-[#ff6b6b] text-sm bg-[#ff6b6b]/10 rounded-xl px-4 py-3 border border-[#ff6b6b]/20" data-testid="auth-error">
                    <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /> {error}
                  </div>
                )}

                <button type="submit" disabled={loading}
                  className="btn-primary w-full py-3.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="auth-submit-btn">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 rounded-full border-2 border-[#0f0e17] border-t-transparent animate-spin" />
                      {mode === 'login' ? t('auth.loggingIn') : t('auth.registering')}
                    </span>
                  ) : (
                    mode === 'login' ? t('auth.login') : t('auth.register')
                  )}
                </button>
              </form>

              <p className="text-center text-sm text-[#e8e8e8] mt-6">
                {mode === 'login' ? t('auth.noAccount') : t('auth.hasAccount')}{' '}
                <button
                  onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                  className="text-[#ff6b6b] hover:underline font-medium"
                  data-testid="auth-toggle-mode-btn">
                  {mode === 'login' ? t('auth.register') : t('auth.login')}
                </button>
              </p>
            </>
          )}
        </div>
      </div>

      <footer className="mt-8 pb-4 flex flex-col items-center gap-2" data-testid="auth-footer">
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
          {i18n.language === 'en' ? (
            <>
              <a href="/en/mentions-legales" className="text-xs text-[#a0a0ab] hover:text-white transition-colors">{t('legal.footerMentions')}</a>
              <a href="/en/privacy-policy" className="text-xs text-[#a0a0ab] hover:text-white transition-colors">{t('legal.footerPrivacy')}</a>
              <a href="/en/terms" className="text-xs text-[#a0a0ab] hover:text-white transition-colors">{t('legal.footerCgu')}</a>
              <a href="/en/cookies" className="text-xs text-[#a0a0ab] hover:text-white transition-colors">{t('legal.footerCookies')}</a>
              <a href="/en/personal-data" className="text-xs text-[#a0a0ab] hover:text-white transition-colors">{t('legal.footerDonnees')}</a>
            </>
          ) : (
            <>
              <a href="/mentions-legales" className="text-xs text-[#a0a0ab] hover:text-white transition-colors">{t('legal.footerMentions')}</a>
              <a href="/politique-de-confidentialite" className="text-xs text-[#a0a0ab] hover:text-white transition-colors">{t('legal.footerPrivacy')}</a>
              <a href="/cgu" className="text-xs text-[#a0a0ab] hover:text-white transition-colors">{t('legal.footerCgu')}</a>
              <a href="/cookies" className="text-xs text-[#a0a0ab] hover:text-white transition-colors">{t('legal.footerCookies')}</a>
              <a href="/donnees-personnelles" className="text-xs text-[#a0a0ab] hover:text-white transition-colors">{t('legal.footerDonnees')}</a>
            </>
          )}
        </div>
        <p className="text-xs text-[#a0a0ab]">{t('footer.copyright')}</p>
      </footer>
    </div>
  );
}
